export interface RubberDataBase {
    finished: boolean
}

export interface ShapeInteractionHandlers<T extends RubberDataBase, S extends GeometricShape> {
    rubberInputInit(shape: S): T;
    rubberInputRenderer(rubberPointData: T, cursorPosition: Point2d): void;
    rubberInputUpdater(rubberPointData: T, cursorPosition: Point2d): T;
    callback(shape: S, rubberPointData: T): GeometricShape;
}

export interface ToolActionEntry<
  TBase extends RubberDataBase = RubberDataBase,
  TSub extends GeometricShape = GeometricShape
> {
  rubberData: TBase;
  shape: TSub;
}

// Helper type for plugin augmentation (avoids self-reference)
export type ToolEntries = {
  [action: string]: ToolActionEntry;
};

// Shape to enforce: category -> subcategory -> { base, sub }
export interface ShapeActionHandlersMapShape {
  [tool: string]: ToolEntries;
}

// This will be augmented by plugins
export interface ShapeActionHandlersMap extends ShapeActionHandlersMapShape {}

type Tool = keyof ShapeActionHandlersMap;
type Action<T extends Tool> = keyof ShapeActionHandlersMap[T];

const registry = new Map<string, ShapeInteractionHandlers<RubberDataBase, GeometricShape>>();


const makeKey = (tool: string, action: string) => {
    return `${tool}::${action}`;
}

const isValidName = (name: string): boolean => {
    return /^[a-zA-Z0-9]+$/.test(name);
}

export const registerInteractionHandlers = <T extends Tool, A extends Action<T>>(tool: T, action: A, interactionHandlers: ShapeInteractionHandlers<ShapeActionHandlersMap[T][A]["rubberData"], ShapeActionHandlersMap[T][A]["shape"]>) => {
    const toolStr = tool as string;
    const actionStr = action as string;

    if (!isValidName(toolStr)) {
        throw new Error(`Invalid type name: "${toolStr}". Only letters and numbers allowed.`);
    }
    if (!isValidName(actionStr)) {
        throw new Error(`Invalid action name: "${actionStr}". Only letters and numbers allowed.`);
    }

    const key = makeKey(toolStr, actionStr);
    if (registry.has(key)) {
        console.log("Warning: overwriting user input methods: ", toolStr, actionStr);
    }

    registry.set(key, interactionHandlers as ShapeInteractionHandlers<RubberDataBase, GeometricShape>);
}

export const getInteractionHandlers = <T extends Tool, A extends Action<T>>(tool: T, action: A): ShapeInteractionHandlers<ShapeActionHandlersMap[T][A]["rubberData"], ShapeActionHandlersMap[T][A]["shape"]> | undefined => {
    const toolStr = tool as string;
    const actionStr = action as string;

    const key = makeKey(toolStr, actionStr);
    return registry.get(key) as ShapeInteractionHandlers<ShapeActionHandlersMap[T][A]["rubberData"], ShapeActionHandlersMap[T][A]["shape"]> | undefined;
}

export const listInteractionHandlers = (): { category: string; subcategory: string }[] => {
    return Array.from(registry.keys()).map(key => {
      const [category, subcategory] = key.split("::");
      return { category, subcategory };
    });
  }